import { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

import { useWebsocketSubscription } from 'hooks/useWebsocketSubscription';
import { indexWorkflows, WorkflowListParams, workflowQueryKeys } from 'services/api/workflows';

import { PORTAL_DISCOVERY_API_NAME } from '../constants';

const NUDGE_DEBOUNCE_MS = 400;
const NUDGE_MAX_WAIT_MS = 1500;

// A backstop for a dropped socket, not a refresh cadence: the nudge fires on every settle, which
// is exactly when a scan's state changes.
const FALLBACK_POLL_INTERVAL_MS = 300_000;

/**
 * The Portal Scans list: every `portal_discovery` run this user can see, in any status.
 *
 * Unfiltered by status on purpose, unlike the Tasks list — a scan's whole story (running,
 * succeeded, found nothing, failed) is the surface, not just the ones wanting attention.
 * Filtered SERVER-side by definition so the lifetime-scoped index isn't downloaded whole,
 * and PAGED for the same reason: it cannot bound itself with a status filter (showing
 * finished sweeps is the feature), and the set grows one run per sweep forever. Newest
 * first, so page one is the live story and history loads as the person scrolls for it.
 *
 * Its own query key, so the Tasks list's rows and these never share a cache entry — but the same
 * `workflowQueryKeys.lists` prefix, so one invalidation reaches both.
 */
const SCAN_LIST_PARAMS: WorkflowListParams = { definitionApiName: [PORTAL_DISCOVERY_API_NAME] };
const SCAN_PAGE_SIZE = 25;

export default function useScans() {
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: workflowQueryKeys.list(SCAN_LIST_PARAMS),
    queryFn: ({ pageParam, signal }) =>
      indexWorkflows({ ...SCAN_LIST_PARAMS, page: pageParam, pageSize: SCAN_PAGE_SIZE }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPageParam < lastPage.meta.totalPages ? lastPageParam + 1 : undefined,
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  });

  const nudged = useMemo(
    () =>
      debounce(() => queryClient.invalidateQueries({ queryKey: workflowQueryKeys.lists }), NUDGE_DEBOUNCE_MS, {
        maxWait: NUDGE_MAX_WAIT_MS,
      }),
    [queryClient]
  );
  useEffect(() => () => nudged.cancel(), [nudged]);

  useWebsocketSubscription({
    channel: 'WorkflowListChannel',
    onReceived: useCallback(() => nudged(), [nudged]),
  });

  return {
    workflows: query.data?.pages.flatMap((page) => page.data) ?? [],
    isLoading: query.isPending,
    isError: query.isError,
    // The raw infinite query, for MoreContentRequester's scroll sentinel.
    query,
  };
}
